import http from "./httpService";

/**
 * @typedef {Object} FedExValidatePayload
 * @property {string} postal_code
 */

/**
 * @typedef {Object} FedExRatesPayload
 * @property {number} cart_id
 * @property {string} postal_code
 * @property {string} [country]
 * @property {boolean} [residenial] - API field spelling (residential)
 */

/**
 * @typedef {Object} FedExRateOption
 * @property {string} serviceType
 * @property {string} serviceName
 * @property {number} price
 * @property {string} [currency]
 * @property {string} [deliveryDate]
 * @property {number} [deliveryDays]
 */

/**
 * Validate a postal code with FedEx.
 * @param {FedExValidatePayload} payload
 */
export async function validatePostalCode(payload) {
  const response = await http.post("/fedex/validate", payload);
  return response.data;
}

/**
 * Get FedEx shipping rates for a cart + destination ZIP.
 * @param {FedExRatesPayload} payload
 */
export async function getFedExRates(payload) {
  const response = await http.post("/fedex/rates", {
    cart_id: Number(payload.cart_id),
    postal_code: String(payload.postal_code || "").trim(),
    country: payload.country || "US",
    residenial: payload.residenial ?? false,
  });
  return response.data;
}

/**
 * @param {number} days
 * @returns {string}
 */
export function formatDeliveryDuration(days) {
  const count = Number(days);
  if (!Number.isFinite(count) || count <= 0) return "";
  return count === 1 ? "1 business day" : `${count} business days`;
}

/**
 * @param {string} dateValue
 * @returns {string}
 */
export function formatDeliveryDate(dateValue) {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Map FedEx rates API rows into checkout shipping option cards.
 * @param {FedExRateOption[]} rates
 */
export function mapFedExRatesToShippingOptions(rates = []) {
  return (Array.isArray(rates) ? rates : []).map((rate, index) => {
    const serviceType = String(rate?.serviceType || "").trim();
    const deliveryDays = Number(rate?.deliveryDays);

    return {
      id: `${serviceType || "rate"}-${index}`,
      serviceType,
      name: String(rate?.serviceName || serviceType || "FedEx").replace(
        /®/g,
        ""
      ),
      duration: formatDeliveryDuration(deliveryDays),
      eta: formatDeliveryDate(rate?.deliveryDate),
      price: Number(rate?.price) || 0,
      currency: rate?.currency || "USD",
      deliveryDate: rate?.deliveryDate || "",
      deliveryDays: Number.isFinite(deliveryDays) ? deliveryDays : null,
    };
  });
}

const fedexService = {
  validatePostalCode,
  getFedExRates,
  mapFedExRatesToShippingOptions,
  formatDeliveryDuration,
  formatDeliveryDate,
};

export default fedexService;
